import React, { useState, useCallback, useEffect, useRef } from "react";
import { CSVLink } from "react-csv";
import { connect } from "react-redux";
import { TbUserEdit } from "react-icons/tb";
import { ButtonGroup } from "react-bootstrap";
import { createColumnHelper } from "@tanstack/react-table";

import { ResendEmailIcon } from "../../../assets";
import { EXPORT_TYPE, STATUS_LABEL } from "../../../system/global-enums";
import {
  EMPTY_STRING,
  PAYLOAD_KEYS,
  adminReduxConstants,
  subAdminsExportHeaders,
  CONFIRMATION_MESSAGE,
} from "../../../constants";
import {
  fileName,
  getSortBy,
  getPageNumber,
  buildFilterQuery,
  customStringSorting,
} from "../../../shared/utility";
import ReactTable from "../../../shared/components/tables/ReactTable";
import SearchField from "../../../shared/components/search/SearchField";
import MainSpinner from "../../../shared/components/spinner/MainSpinner";
import RoundedButton from "../../../shared/components/buttons/RoundedButton";
import ExportDropdown from "../../../shared/components/export/ExportDropdown";
import ConfirmationModal from "../../../shared/components/modals/ConfirmationModal";
import MainContentWrapper from "../../../shared/components/main-content-wrapper/MainContentWrapper";
import { adminActions, systemActions } from "../../../redux/actions";
import AddSubAdmin from "./AddSubAdmin";

const { FILTER_QUERY, PAGE_SIZE, PAGE_NUMBER, SEARCH } = PAYLOAD_KEYS;
const {
  ADD_SUB_ADMIN,
  GET_SUB_ADMINS,
  DELETE_SUB_ADMIN,
  SUB_ADMINS_PAGINATION,
  GET_SUB_ADMINS_EXPORT_ALL_DATA,
} = adminReduxConstants;

const SubAdmins = ({
  user,
  subAdmins,
  spinnerActions,
  subAdminsPagination,
  subAdminsAllItemsForExport,
  getSubAdmins,
  resendAdminEmail,
  updatePagination,
  updateAdminStatus,
  resetAllExportItems,
  getAllSubAdminsItemsForExport,
  setSelectedRowId,
  selectedRowId,
}) => {
  const csvLinkRef = useRef();
  const [exportData, setExportData] = useState([]);
  const [currentSubAdmin, setCurrentSubAdmin] = useState(null);
  const [openSubAdminModal, setOpenSubAdminModal] = useState(false);
  const [sorting, setSorting] = useState({ id: "id", desc: true });
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const handleOpenAdminModal = () => setOpenSubAdminModal(true);

  const handleCloseAdminModal = (refetch) => {
    setOpenSubAdminModal(false);
    if (refetch) {
      getSubAdmins(subAdminsPagination);
    }
    setCurrentSubAdmin(null);
  };

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor("name", {
      header: <div className="column-head">Name</div>,
      cell: ({ row: { original } }) => (
        <div title={original.name} className="line-clamp">
          {original.name}
        </div>
      ),
      enableSorting: true,
      sortingFn: customStringSorting,
      size: 150,
    }),
    columnHelper.accessor("email", {
      header: <div className="column-head">Email</div>,
      cell: ({ row: { original } }) => (
        <div title={original.email} className="line-clamp">
          {original.email}
        </div>
      ),
      enableSorting: true,
      sortingFn: customStringSorting,
      size: 150,
    }),
    columnHelper.accessor("phoneNumber", {
      header: <div className="column-head">Phone Number</div>,
      cell: ({ row: { original } }) => <div>+{original.phoneNumber}</div>,
      enableSorting: true,
    }),
    columnHelper.accessor("status", {
      enableSorting: false,
      header: <div className="column-head">Status</div>,
      cell: ({ row: { original } }) => (
        <div>
          {original.isActive ? (
            <div className="admin-switch-status">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  disabled={original.id === user.userId}
                  checked={original.status}
                  onChange={() => {
                    setSelectedRowId(original.id);
                    setShowConfirmationModal(true);
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="invite-sent">Invite Sent</div>
          )}
        </div>
      ),
    }),
    columnHelper.accessor("action", {
      header: <div className="column-head text-end">Action</div>,
      enableSorting: false,
      cell: ({ row: { original } }) => (
        <div className=" d-flex justify-content-end pe-2 gap-2">
          {!original.isActive && (
            <img
              className="pointer"
              src={ResendEmailIcon}
              onClick={() => resendAdminEmail(original.id)}
              alt={EMPTY_STRING}
            />
          )}
          <TbUserEdit
            className="sub-admin-edit-icon"
            onClick={() => {
              setCurrentSubAdmin(original);
              handleOpenAdminModal();
            }}
          />
        </div>
      ),
    }),
  ];

  const spinner = useCallback(
    (type) => {
      return spinnerActions.findIndex((s) => s === type);
    },
    // eslint-disable-next-line
    [spinnerActions]
  );

  const onConfirmation = () => {
    setShowConfirmationModal(false);
    updateAdminStatus(selectedRowId, () =>
      getSubAdmins({ ...subAdminsPagination, orderBy: getSortBy(sorting) })
    );
  };

  // Used to export CSV
  const exportCSV = () => {
    setTimeout(() => {
      csvLinkRef.current.link.click();
    }, 1000);
  };

  const updatedRecord = (items) => {
    return items.map((item) => {
      return {
        ...item,
        phoneNumber: `\t+${item.phoneNumber}`,
        status: item.isActive
          ? item.status
            ? STATUS_LABEL.ACTIVE
            : STATUS_LABEL.INACTIVE
          : STATUS_LABEL.INVITE_SENT,
      };
    });
  };

  // Get data for export
  const getExportData = (type) => {
    if (type === EXPORT_TYPE.GRID) {
      setExportData(updatedRecord(subAdmins));
      exportCSV();
    }
    if (type === EXPORT_TYPE.ALL) {
      const updatedPayload = {
        ...subAdminsPagination,
        [PAGE_NUMBER]: 0,
        [PAGE_SIZE]: subAdminsPagination.total,
        [FILTER_QUERY]: getFilterQuery(subAdminsPagination.search),
        orderBy: getSortBy(sorting),
      };
      getAllSubAdminsItemsForExport(updatedPayload);
    }
  };

  const getFilterQuery = (data) => {
    const searchFields = ["Name", "Email", "PhoneNumber"];
    return buildFilterQuery(data, searchFields);
  };

  const handleUpdatePagination = (data, type) => {
    const payload = {
      ...subAdminsPagination,
      [SEARCH]: type === SEARCH ? data : subAdminsPagination.search,
      [PAGE_SIZE]: type === PAGE_SIZE ? data : subAdminsPagination.pageSize,
      [PAGE_NUMBER]: getPageNumber(type, data, subAdminsPagination),
    };

    updatePagination(payload, SUB_ADMINS_PAGINATION);
  };

  useEffect(() => {
    return () => {
      updatePagination(
        {
          [PAGE_SIZE]: 10,
          [PAGE_NUMBER]: 0,
          [SEARCH]: EMPTY_STRING,
          [FILTER_QUERY]: EMPTY_STRING,
        },
        SUB_ADMINS_PAGINATION
      );
    };
  }, []);

  useEffect(() => {
    const updatedPayload = {
      ...subAdminsPagination,
      [FILTER_QUERY]: getFilterQuery(subAdminsPagination.search),
      orderBy: getSortBy(sorting),
    };

    getSubAdmins(updatedPayload);
    // eslint-disable-next-line
  }, [
    subAdminsPagination.search,
    subAdminsPagination.pageNumber,
    subAdminsPagination.pageSize,
    sorting,
  ]);

  useEffect(() => {
    if (subAdminsAllItemsForExport.length) {
      setExportData(updatedRecord(subAdminsAllItemsForExport));
      exportCSV();
      resetAllExportItems(GET_SUB_ADMINS_EXPORT_ALL_DATA);
    }
    // eslint-disable-next-line
  }, [subAdminsAllItemsForExport]);

  return (
    <MainContentWrapper>
      {(spinner(GET_SUB_ADMINS) > -1 ||
        spinner(ADD_SUB_ADMIN) > -1 ||
        spinner(DELETE_SUB_ADMIN) > -1) && (
        <MainSpinner className="admin-spinner" />
      )}
      <ConfirmationModal
        onCancel={() => setShowConfirmationModal(false)}
        onConfirm={() => onConfirmation()}
        show={showConfirmationModal}
        message={CONFIRMATION_MESSAGE}
      />
      <AddSubAdmin
        onClose={handleCloseAdminModal}
        show={openSubAdminModal}
        currentData={currentSubAdmin}
      />
      <div className="admin-page-heading">Sub Admins</div>
      <div className="d-flex justify-content-between align-items-center flex-wrap">
        <SearchField
          value={subAdminsPagination.search}
          handleSearch={(val) => handleUpdatePagination(val, SEARCH)}
        />
        <ButtonGroup className="button-group">
          <RoundedButton
            className="add-admin-button mt-3 mt-sm-4"
            onClick={handleOpenAdminModal}
          >
            Add Sub Admin
          </RoundedButton>
          <ExportDropdown className="mt-3 mt-sm-4" onExport={getExportData} />
        </ButtonGroup>
      </div>
      <ReactTable
        data={subAdmins}
        columns={columns}
        paginationData={{
          currentPage: subAdminsPagination.pageNumber,
          totalItems: subAdminsPagination?.total ?? 0,
          pageSize: subAdminsPagination.pageSize,
          onPageChange: (page) => handleUpdatePagination(page, PAGE_NUMBER),
          onPageSizeChange: (pageSize) =>
            handleUpdatePagination(pageSize, PAGE_SIZE),
        }}
        sortedBy={sorting}
        onColumnSort={(sortBy) => setSorting(sortBy)}
      />
      <CSVLink
        target="_blank"
        ref={csvLinkRef}
        className="hidden"
        headers={subAdminsExportHeaders}
        data={exportData}
        filename={fileName("Sub Admins")}
      />
    </MainContentWrapper>
  );
};

const mapStateToProps = (state) => ({
  user: state.authReducer.user,
  subAdmins: state.adminReducer.subAdmins,
  spinnerActions: state.systemReducer.spinnerActions,
  subAdminsPagination: state.adminReducer.subAdminsPagination,
  subAdminsAllItemsForExport: state.adminReducer.subAdminsAllItemsForExport,
  selectedRowId: state.systemReducer.selectedRowId,
});

const mapDispatchToProps = (dispatch) => ({
  getSubAdmins: (data) => dispatch(adminActions.getSubAdmins(data)),
  resendAdminEmail: (id) => dispatch(adminActions.resendAdminEmail(id)),
  updatePagination: (data, type) =>
    dispatch(adminActions.updatePagination(data, type)),
  updateAdminStatus: (id, func) =>
    dispatch(adminActions.updateAdminStatus(id, func)),
  getAllSubAdminsItemsForExport: (data) =>
    dispatch(adminActions.getAllSubAdminsItemsForExport(data)),
  resetAllExportItems: (type) =>
    dispatch(adminActions.resetAllExportItems(type)),
  setSelectedRowId: (id) => dispatch(systemActions.setSelectedRowId(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SubAdmins);
